import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Search, ChevronRight, ChevronLeft, X, Library } from 'lucide-react';
import { WS, SKILL_DOMAINS, type SkillItem } from './workspaceData.ts';
import SkillCard from './SkillCard.tsx';
import { useSkillCatalogue } from '../../hooks/useSkillCatalogue.ts';
import { alpha } from '../../utils/alpha';

interface SkillLibrarySidebarProps {
  activeSkillIds: string[];
  onToggleSkill: (id: string) => void;
  mcpServers?: any[];
  selectedMcpIds?: string[];
  onToggleMcp?: (id: string) => void;
  allowedDomains?: string[];
  // Collapsed rail mode — width is driven by the parent (WorkspaceLayout).
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
}

// Sidebar simplified: show domains/skills based on allowedDomains prop (user-selected)

export default function SkillLibrarySidebar({
  activeSkillIds,
  onToggleSkill,
  mcpServers = [],
  selectedMcpIds = [],
  onToggleMcp,
  allowedDomains,
  collapsed = false,
  onToggleCollapsed,
}: SkillLibrarySidebarProps) {
  const [search, setSearch]                 = useState('');
  const [openDomains, setOpenDomains]       = useState<Set<string>>(new Set(['research', 'coding', 'marketing']));
  const searchRef = useRef<HTMLInputElement>(null);

  // Focus the search box whenever the rail expands, so "click to expand"
  // flows straight into "start typing" with no extra click.
  useEffect(() => {
    if (!collapsed) searchRef.current?.focus();
  }, [collapsed]);

  // Fetch skill catalogue from server (cached across mounts)
  const { skills: catalogueSkills, loading: catalogueLoading } = useSkillCatalogue();

  const toggleDomain = (id: string) => {
    setOpenDomains(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // domain selection is handled from Skills page; sidebar only reflects allowedDomains

  const filteredSkills = useMemo(() => {
    // Always include Lyra built-in skills (ids starting with `lyra:`), even when
    // `allowedDomains` is present. This ensures core integrations like Gmail
    // Reader and Calendar Assistant remain discoverable in the builder.
    let base = catalogueSkills;
    if (allowedDomains && allowedDomains.length > 0) {
      base = base.filter(s => allowedDomains.includes(s.domain) || String(s.id).startsWith('lyra:') || s.lyraSkill === true);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      base = base.filter(s =>
        s.name.toLowerCase().includes(q) ||
        s.description.toLowerCase().includes(q) ||
        (s.tags || []).some((t: string) => t.includes(q)) ||
        s.domain.includes(q)
      );
    }
    return base;
  }, [search, allowedDomains, catalogueSkills]);

  const skillsByDomain = useMemo(() => {
    const map: Record<string, SkillItem[]> = {};
    const visibleDomains = allowedDomains && allowedDomains.length > 0
      ? SKILL_DOMAINS.filter(d => allowedDomains.includes(d.id))
      : SKILL_DOMAINS;
    visibleDomains.forEach(d => { map[d.id] = []; });
    filteredSkills.forEach(s => {
      if (map[s.domain]) map[s.domain].push(s);
    });
    return map;
  }, [filteredSkills, allowedDomains]);

  const isSearching = search.trim().length > 0;

  if (collapsed) {
    return (
      <div style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 14,
        padding: '16px 0',
        background: WS.surface,
        borderRight: `1px solid ${WS.border}`,
      }}>
        <button
          onClick={onToggleCollapsed}
          title="Expand Skill Library"
          style={{
            width: 32, height: 32, borderRadius: 9,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: WS.surface2, border: `1px solid ${WS.border}`, cursor: 'pointer',
            color: WS.ink3,
          }}
        >
          <ChevronRight size={14} />
        </button>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          <Library size={16} color={WS.ink4} />
          {activeSkillIds.length > 0 && (
            <span style={{
              fontSize: 9.5, fontWeight: 700, padding: '1px 5px', borderRadius: 99,
              background: WS.accentBg, color: WS.accentHov, border: `1px solid ${WS.accentBd}`,
            }}>
              {activeSkillIds.length}
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      background: WS.surface,
      borderRight: `1px solid ${WS.border}`,
      overflow: 'hidden',
    }}>

      {/* ── Sticky Header ── */}
      <div style={{
        padding: '16px 14px 12px',
        borderBottom: `1px solid ${WS.border}`,
        flexShrink: 0,
        background: WS.surface,
      }}>
        {/* Title */}
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
          <span style={{
            fontSize: 11,
            fontWeight: 600,
            color: WS.ink4,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            flex: 1,
          }}>
            Skill Library
          </span>
          {onToggleCollapsed && (
            <button
              onClick={onToggleCollapsed}
              title="Collapse Skill Library"
              style={{
                width: 22, height: 22, borderRadius: 6,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'transparent', border: 'none', cursor: 'pointer', color: WS.ink4,
              }}
              onMouseEnter={e => { e.currentTarget.style.background = WS.surface2; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              <ChevronLeft size={14} />
            </button>
          )}
        </div>

        {/* Search */}
        <div style={{ position: 'relative', marginBottom: 10 }}>
          <Search
            size={13}
            color={WS.ink4}
            style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
          />
          <input
            ref={searchRef}
            className="ws-input"
            value={search}
            onChange={e => { setSearch(e.target.value); }}
            placeholder="Search skills, domains, tools..."
            style={{
              width: '100%',
              background: WS.surface3,
              border: `1px solid ${WS.border}`,
              borderRadius: 9,
              padding: '7px 10px 7px 30px',
              fontSize: 12,
              color: WS.ink,
              fontFamily: 'inherit',
            }}
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              style={{
                position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', cursor: 'pointer', padding: 2,
                display: 'flex', alignItems: 'center',
              }}
            >
              <X size={11} color={WS.ink4} />
            </button>
          )}
        </div>

        {/* Quick filters removed — sidebar shows only user-selected domains/skills */}

        {isSearching && (
          <div style={{ fontSize: 11, color: WS.ink4 }}>
            {filteredSkills.length} result{filteredSkills.length !== 1 ? 's' : ''} for "{search}"
          </div>
        )}
      </div>

      {/* ── Scrollable Content ── */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 10px 16px' }}>

        {/* Loading skeleton */}
        {catalogueLoading && !isSearching && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '12px 0' }}>
            {[1, 2, 3, 4].map(i => (
              <div key={i} style={{ height: 32, borderRadius: 8, background: WS.surface3, opacity: 0.7, animation: 'ws-fadein 0.4s ease' }} />
            ))}
          </div>
        )}

        {/* Flat search results */}
        {!catalogueLoading && isSearching ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {filteredSkills.length === 0 ? (
              <div style={{
                textAlign: 'center', padding: '40px 20px',
                color: WS.ink4, fontSize: 12,
              }}>
                <Search size={28} color={WS.ink4} style={{ marginBottom: 8 }} />
                No skills matching "{search}"
              </div>
            ) : (
              filteredSkills.map(skill => (
                <SkillCard
                  key={skill.id}
                  skill={skill}
                  isActive={activeSkillIds.includes(skill.id)}
                  onToggle={onToggleSkill}
                  animate
                />
              ))
            )}
          </div>
        ) : (
          /* Domain accordions */
          !catalogueLoading && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {((allowedDomains && allowedDomains.length > 0 ? SKILL_DOMAINS.filter(d => allowedDomains.includes(d.id)) : SKILL_DOMAINS).filter(d => d.id !== 'mcp')).map(domain => {
              const DomainIcon = domain.icon;
              const domainSkills = skillsByDomain[domain.id] || [];
              if (domainSkills.length === 0) return null;
              const isOpen = openDomains.has(domain.id);
              const activeCount = domainSkills.filter(s => activeSkillIds.includes(s.id)).length;

              return (
                <div key={domain.id} style={{ marginBottom: 2 }}>
                  {/* Domain Header */}
                  <button
                    onClick={() => toggleDomain(domain.id)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '7px 8px',
                      borderRadius: 8,
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      transition: 'background 0.12s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = WS.surface2; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <ChevronRight
                      size={12}
                      color={WS.ink4}
                      className={`ws-domain-toggle ${isOpen ? 'open' : ''}`}
                    />
                    <DomainIcon size={14} color={isOpen ? domain.color : WS.ink4} style={{ flexShrink: 0 }} />
                    <span style={{ fontSize: 12, fontWeight: 500, color: WS.ink2, flex: 1, textAlign: 'left' }}>
                      {domain.label}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }} onClick={e => e.stopPropagation()}>
                      {activeCount > 0 && (
                        <span style={{
                          fontSize: 9, fontWeight: 700,
                          padding: '1px 5px', borderRadius: 99,
                          background: alpha(domain.color, '20'),
                          color: domain.color,
                          border: `1px solid ${alpha(domain.color, '35')}`,
                        }}>
                          {activeCount}
                        </span>
                      )}
                      <span style={{
                        fontSize: 10, color: WS.ink4,
                        background: WS.surface3,
                        padding: '1px 5px', borderRadius: 99,
                        border: `1px solid ${WS.border}`,
                      }}>
                        {domainSkills.length}
                      </span>
                    </div>
                  </button>

                  {/* Skills list */}
                  {isOpen && (
                    <div style={{
                      display: 'flex', flexDirection: 'column', gap: 3,
                      padding: '3px 0 6px 4px',
                      animation: 'ws-fadein 0.15s ease',
                    }}>
                      {domainSkills.map(skill => (
                        <SkillCard
                          key={skill.id}
                          skill={skill}
                          isActive={activeSkillIds.includes(skill.id)}
                          onToggle={onToggleSkill}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {/* MCP Tools Section */}
            {mcpServers.length > 0 && (
              <div style={{ marginTop: 8, borderTop: `1px solid ${WS.border}`, paddingTop: 8 }}>
                <button
                  onClick={() => toggleDomain('mcp')}
                  style={{
                    width: '100%',
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '7px 8px', borderRadius: 8,
                    border: 'none', background: 'transparent',
                    cursor: 'pointer', fontFamily: 'inherit', transition: 'background 0.12s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = WS.surface2; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <ChevronRight
                    size={12}
                    color={WS.ink4}
                    className={`ws-domain-toggle ${openDomains.has('mcp') ? 'open' : ''}`}
                  />
                  <span style={{ fontSize: 13 }}>🔌</span>
                  <span style={{ fontSize: 12, fontWeight: 500, color: WS.ink2, flex: 1, textAlign: 'left' }}>
                    MCP Tools
                  </span>
                  <span style={{
                    fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 99,
                    background: WS.greenBg, color: WS.greenText, border: `1px solid ${WS.greenBd}`,
                  }}>
                    {mcpServers.length}
                  </span>
                </button>

                {openDomains.has('mcp') && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3, padding: '3px 0 6px 4px', animation: 'ws-fadein 0.15s ease' }}>
                    {mcpServers.map((srv: any) => {
                      const active = selectedMcpIds.includes(srv._id);
                      return (
                        <div
                          key={srv._id}
                          onClick={() => onToggleMcp?.(srv._id)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 9,
                            padding: '8px 10px', borderRadius: 10, cursor: 'pointer',
                            background: active ? WS.greenBg : WS.surface2,
                            border: `1px solid ${active ? WS.greenBd : WS.border}`,
                            transition: 'all 0.14s',
                          }}
                          onMouseEnter={e => { if (!active) e.currentTarget.style.background = WS.surface3; }}
                          onMouseLeave={e => { if (!active) e.currentTarget.style.background = WS.surface2; }}
                        >
                          <span style={{ fontSize: 14 }}>🔌</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 12, fontWeight: 500, color: active ? WS.greenText : WS.ink2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {srv.displayName || srv.name}
                            </div>
                            <div style={{ fontSize: 10, color: WS.ink4 }}>{(srv.tools || []).length} tools</div>
                          </div>
                          {active && (
                            <div style={{ width: 14, height: 14, borderRadius: '50%', background: WS.green, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <span style={{ fontSize: 8, color: 'var(--app-on-accent)' }}>✓</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
          )
        )}
      </div>
    </div>
  );
}
